import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import WorldMap from "@/components/WorldMap";
import Navbar from "@/components/Navbar";
import Leaderboard from "@/components/Leaderboard";
import AuthModal from "@/components/AuthModal";
import CountryModal from "@/components/CountryModal";
import BidderProfilePopup from "@/components/BidderProfilePopup";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://topnationapp-1-api.onrender.com";

const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { user } = useAuth();

  const [countries, setCountries] = useState([]);
  const [liveBids, setLiveBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    fetchCountries();
    fetchLiveBids();

    const interval = setInterval(fetchLiveBids, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setSelectedProfile(e.detail);
    };

    window.addEventListener("openProfile", handler);
    return () => window.removeEventListener("openProfile", handler);
  }, []);

  useEffect(() => {
  const timer = setTimeout(() => {
    setShowSplash(false);
  }, 1600);

  return () => clearTimeout(timer);
}, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load countries");
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveBids = async () => {
    try {
      const response = await axios.get(`${API}/live-bids`);
      if (response.data && response.data.length > 0) {
        setLiveBids(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCountryClick = (country) => {
    if (!user) {
      toast.info("Login to place bids. You can still view the country.");
    }

    setSelectedCountry(country);
    setShowCountryModal(true);
  };

  const handleBidSuccess = () => {
    fetchCountries();
    fetchLiveBids();
    setShowCountryModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#FFD700] text-2xl font-['Cinzel']">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative" data-testid="home-page">
    {showSplash && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "#050505",
      zIndex: 999999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      color: "#FFD700",
      fontFamily: "Cinzel, serif",
    }}
  >
    <div
      style={{
        fontSize: "42px",
        fontWeight: "bold",
        letterSpacing: "3px",
        textShadow: "0 0 18px rgba(255,215,0,0.7)",
        marginBottom: "18px",
      }}
    >
      TOPNATIONAPP
    </div>

   <img
  src="/globe.png"
  alt="TopNation Globe"
  style={{
    width: "78px",
    height: "78px",
    marginBottom: "18px",
    animation: "globePulse 2.5s ease-in-out infinite",
    filter:
      "drop-shadow(0 0 6px #FFD700) drop-shadow(0 0 14px #FFD700) drop-shadow(0 0 26px #FFD700)",
  }}
/>

    <div
      style={{
        fontSize: "16px",
        opacity: 0.85,
      }}
    >
      Loading world map...
    </div>
  </div>
)}
      <div className="noise-overlay" />

      <Navbar onLoginClick={() => setShowAuthModal(true)} />

      <main className="relative z-10 w-full overflow-x-hidden">
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-6 px-4 md:px-6 pb-6">

          {/* MAPA */}
          <div className="lg:col-span-3 w-full relative z-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 text-center"
            >
              <h1 className="text-5xl md:text-7xl font-['Cinzel'] font-bold text-[#FFD700] uppercase tracking-tight mb-4">
                Own the World
              </h1>

              <div className="text-xl md:text-2xl text-[#FFD700] font-semibold mb-6 font-['Cinzel']">
                The World's First Global Bidding Map
              </div>

              <motion.div className="mb-4 w-full flex flex-col items-start text-sm md:text-base text-gray-300 space-y-2">
                <div className="live-bids flex items-center gap-2">
                  <span className="live-globe">🌍</span>
                  <span className="text-[#FFD700] font-semibold">LIVE BIDS</span>
                </div>

                <div className="live-ticker-wrapper w-full">
                  <div className="live-ticker">
                    {(liveBids.length
                      ? liveBids
                      : [{ bidder: "FreedomFighterUA", amount: 1, country: "Ukraine" }]
                    )
                      .concat(
                        liveBids.length
                          ? liveBids
                          : [{ bidder: "FreedomFighterUA", amount: 1, country: "Ukraine" }]
                      )
                      .map((bid, index) => (
                        <span key={index}>
                          🔥 {bid.bidder} bid €{bid.amount} on {bid.country}
                        </span>
                      ))}
                  </div>
                </div>
              </motion.div>

              <p className="text-lg md:text-2xl text-[#FFD700] font-semibold leading-relaxed max-w-3xl mx-auto font-['Cinzel']">
                Claim any nation.
                <br />
                Conquer the world through the power of bidding.
                <br />
                Climb the global leaderboard.
              </p>
            </motion.div>

{/* HOW IT WORKS BUTTON */}
<button
  onClick={() => setShowHowItWorks(true)}
  style={{
  position: "fixed",
  bottom: "18px",
  left: "18px",
  zIndex: 9999,
  width: "52px",
  height: "52px",
  borderRadius: "50%",
  background: "rgba(0,0,0,0.85)",
  border: "2px solid #FFD700",
  color: "#FFD700",
  fontSize: "28px",
  fontWeight: "bold",
  fontStyle: "italic",
  fontFamily: "serif",
  cursor: "pointer",
  boxShadow: "0 0 15px rgba(255,215,0,0.6)",
  backdropFilter: "blur(8px)",
}}
>
  i
</button>

            {/* MAPA CONTAINER */}
            <div className="w-full h-[50vh] md:h-[600px] relative z-0">
              <WorldMap
                countries={countries}
                onCountryClick={handleCountryClick}
                selectedCountry={selectedCountry}
              />
            </div>
          </div>

          {/* LEADERBOARD */}
          <div className="lg:col-span-1 w-full relative z-10">
            <Leaderboard countries={countries} />
          </div>

        </div>
      </main>
            {/* HOW IT WORKS POPUP */}
{showHowItWorks && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.75)",
      zIndex: 99999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px",
    }}
  >
    <div
      style={{
        background: "#050505",
        border: "2px solid #FFD700",
        borderRadius: "20px",
        padding: "28px",
        maxWidth: "420px",
        width: "100%",
        color: "#FFD700",
        textAlign: "center",
        boxShadow: "0 0 30px rgba(255,215,0,0.35)",
      }}
    >
      <h2
        style={{
          fontSize: "28px",
          marginBottom: "22px",
          fontWeight: "bold",
        }}
      >
        HOW IT WORKS
      </h2>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          fontSize: "18px",
          lineHeight: "1.5",
        }}
      >
        <div>🌍 Select any country</div>
        <div>💰 Place your bid</div>
        <div>🏆 Become the #1 owner</div>
        <div>🌍 Climb the global leaderboard</div>
        <div>🔥 Compete with the world</div>
      </div>

      <button
        onClick={() => setShowHowItWorks(false)}
        style={{
          marginTop: "28px",
          background: "#FFD700",
          color: "#000",
          border: "none",
          padding: "12px 24px",
          borderRadius: "12px",
          cursor: "pointer",
          fontWeight: "bold",
          fontSize: "16px",
        }}
      >
        CLOSE
      </button>
    </div>
  </div>
)}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      <CountryModal
        country={selectedCountry}
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onBidSuccess={handleBidSuccess}
      />

      <BidderProfilePopup
        profile={selectedProfile}
        onClose={() => setSelectedProfile(null)}
      />
    </div>
  );
};

export default Home;
